import { useState, useEffect, useMemo } from "react";
import { Card, CardHeader, CardBody, Col, Row, Button, Alert, Spinner } from "reactstrap";
import { saveAs } from "file-saver";
import Swal from "sweetalert2";
import { FilterSection } from "../FilterSection";
import { MasterSheetTable } from "./MasterSheetTable";
import { PropertySelector } from "./PropertySelector";
import axios from "../../../api/axios";
import { ENDPOINTS, getMastersheet, getArchiveMastersheet, addTenant, updateTenant } from "../TenantForm/Helpers/api";
import { PreviewTenantForm } from "./PreviewTenantForm/PreviewTenantForm";

const ArchivedMastersheet = () => {
    const parsedAuthUser = JSON.parse(window.localStorage.getItem("authUser") || '{}');
    const userData = parsedAuthUser.userData || parsedAuthUser;
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [masterSheetData, setMasterSheetData] = useState({ items: [], mastersheetId: null });
    const [isCreatingNew, setIsCreatingNew] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [currentProperty, setCurrentProperty] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [filterData, setFilterData] = useState({
        property_name: "",
        property_id: "",
        building_name: "",
    });
    // Everything loads in one go like the live mastersheet; a page holds 100 units, so
    // these only come into play once a building has archived more than 100 tenancies.
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState(null);
    // The same dynamic filter panel as the live sheet. The archive filters the loaded
    // page on the client — with a 100-unit page that is the whole building's archive
    // for all but the largest buildings.
    const [filterValues, setFilterValues] = useState({});
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilterValues((prev) => ({ ...prev, [name]: value }));
    };
    useEffect(() => {
        setFilterValues({});
    }, [filterData.property_id, filterData.building_name]);

    const isValidSelection = !!filterData.property_id && (filterData.building_list?.length === 0 || !!filterData.building_name);

    const fetchMasterSheet = async (targetPage = 1) => {
        const { property_id, building_name } = filterData;
        if (!property_id) return;

        setIsLoading(true);
        try {
            // Fetch main MasterSheet to get mastersheetId
            const { data: mainData } = await getMastersheet({
                property_id,
                building_name,
            });

            // Fetch archived MasterSheet using mastersheetId
            let archivedData = { items: [], pagination: null };
            if (mainData?._id) {
                const { data } = await getArchiveMastersheet({ mastersheetId: mainData._id, page: targetPage });
                archivedData = data;
            }

            // Dictionary order by unit number (owner's direction, 2026-08-31): a plain
            // lexicographic sort, the way a dictionary lists words — "101", "1010", "102".
            // The server pages in the same order (mastersheet-archive-item.controller).
            const sortedItems = (archivedData.items || []).sort((a, b) =>
                String(a.unitNo ?? "").localeCompare(String(b.unitNo ?? ""))
            );

            setMasterSheetData({
                ...mainData,
                items: sortedItems,
                mastersheetId: mainData._id,
            });
            setPagination(archivedData.pagination || null);
            setPage(targetPage);
        } catch (error) {
            const message = error?.response?.data?.message || "Failed to fetch Archived Master Sheet.";
            if (message === "Building name is required for this property") {
                return;
            }
            if (message === "Master sheet not found for the given property") {
                setMasterSheetData({ items: [], mastersheetId: null });
            }
            Swal.fire("Error", message, "error");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!isValidSelection) return;
        // A new property/building always starts back at the first page.
        fetchMasterSheet(1);
    }, [filterData.property_id, filterData.building_name]);

    const handleMasterSheetAction = async () => {
        try {
            const result = await Swal.fire({
                title: "Choose an Action",
                text: `Do you want to import a file to the ${filterData.property_name} Master Sheet or download the Archived Master Sheet?`,
                icon: "question",
                showCancelButton: true,
                showDenyButton: true,
                confirmButtonText: "Download Archived Master Sheet",
                denyButtonText: "Import File",
                cancelButtonText: "Cancel",
            });

            if (result.isConfirmed) {
                const downloadConfirm = await Swal.fire({
                    title: "Are you sure?",
                    text: "Do you want to download the Archived Master Sheet Excel file?",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonText: "Yes, download!",
                    cancelButtonText: "No, cancel",
                });

                if (downloadConfirm.isConfirmed) {
                    const response = await axios.post(
                        ENDPOINTS.DOWNLOAD_MASTERSHEET_ITEMS,
                        { ...filterData, mastersheet_id: masterSheetData.mastersheetId, archived: true },
                        { responseType: "blob" }
                    );

                    const file = new Blob([response.data], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    });
                    saveAs(file, `ArchivedMasterSheet.xlsx`);
                    Swal.fire("Success!", "The Excel file has been downloaded.", "success");
                }
            } else if (result.isDenied) {
                const fileInput = document.createElement("input");
                fileInput.type = "file";
                fileInput.accept = ".xlsx, .xls";
                fileInput.style.display = "none";

                fileInput.addEventListener("change", async (event) => {
                    const file = event.target.files[0];
                    if (file) {
                        const formData = new FormData();
                        formData.append("file", file);
                        formData.append("property_id", filterData.property_id || "");
                        formData.append("building_name", filterData.building_name || "");
                        if (masterSheetData.mastersheetId) {
                            formData.append("mastersheet_id", masterSheetData.mastersheetId);
                        }

                        try {
                            setIsLoading(true);
                            const response = await axios.post(ENDPOINTS.UPLOAD_MASTERSHEET_ITEMS, formData, {
                                headers: { "Content-Type": "multipart/form-data" },
                            });

                            if (response.data.success) {
                                Swal.fire("Success!", "The file has been imported successfully.", "success");
                                fetchMasterSheet();
                            } else {
                                const errorMessages = response.data.errors.join("<br>");
                                Swal.fire("Validation Errors", errorMessages, "error");
                            }
                        } catch (error) {
                            if (error.response && error.response.status === 400) {
                                const errorData = error.response.data;
                                if (errorData.errors && Array.isArray(errorData.errors)) {
                                    const errorMessages = errorData.errors.join("<br>");
                                    Swal.fire("Validation Errors", errorMessages, "error");
                                } else {
                                    Swal.fire("Import Error", errorData.message || "Invalid data in the uploaded file.", "error");
                                }
                            } else {
                                Swal.fire("Error", "An error occurred while importing the file.", "error");
                            }
                        } finally {
                            setIsLoading(false);
                        }
                    }
                });
                document.body.appendChild(fileInput);
                fileInput.click();
                document.body.removeChild(fileInput);
            }
        } catch (error) {
            Swal.fire("Error", "An unexpected error occurred.", "error");
        }
    };

    const handleExportMastersheetAction = async () => {
        try {
            const confirmExport = await Swal.fire({
                title: "Download Archived Master Sheet",
                text: `Do you want to download the Archived Master Sheet for ${filterData.property_name}?`,
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Yes, download it!",
                cancelButtonText: "Cancel",
            });

            if (confirmExport.isConfirmed) {
                const response = await axios.post(
                    ENDPOINTS.DOWNLOAD_MASTERSHEET_ITEMS,
                    { ...filterData, mastersheet_id: masterSheetData.mastersheetId, archived: true },
                    { responseType: "blob" }
                );

                const file = new Blob([response.data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });

                saveAs(file, `ArchivedMasterSheet.xlsx`);
                Swal.fire("Success!", "The Excel file has been downloaded.", "success");
            }
        } catch (error) {
            Swal.fire("Error", "An error occurred while exporting the Archived Master Sheet.", "error");
        }
    };

    const handleAddTenant = () => {
        setCurrentProperty(null);
        setEditMode(false);
        setShowForm(true);
    };

    const handleEditTenant = (tenantData) => {
        setCurrentProperty(tenantData);
        setEditMode(true);
        setShowForm(true);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setCurrentProperty(null);
        setEditMode(false);
    };

    const handleFormSubmit = async (formData) => {
        try {
            setIsLoading(true);
            const payload = {
                values: formData,
                property_id: filterData.property_id,
                building_name: filterData.building_name,
                mastersheet_id: masterSheetData.mastersheetId,
            };

            const response = editMode
                ? await updateTenant({ ...payload, _id: currentProperty._id })
                : await addTenant(payload);

            if (response?.status === 200 || response?.status === 201) {
                Swal.fire(
                    "Success",
                    `Tenant ${editMode ? "updated" : "added"} successfully`,
                    "success"
                );
                handleCloseForm();
                fetchMasterSheet(page);
            } else {
                const msg = response?.data?.message || `Failed to ${editMode ? "update" : "add"} tenant.`;
                Swal.fire("Error", msg, "error");
            }
        } catch (error) {
            if (error.response?.data?.errors?.length) {
                const messages = error.response.data.errors
                    .map((err) => `• ${err.message}`)
                    .join("\n");

                Swal.fire({
                    icon: "error",
                    title: "Validation Errors",
                    html: `<pre style="text-align: left;">${messages}</pre>`,
                });
            } else {
                const message =
                    error.response?.data?.message ||
                    error.message ||
                    `An unexpected error occurred while trying to ${editMode ? "update" : "add"} the tenant.`;
                Swal.fire("Error", message, "error");
            }
        } finally {
            setIsLoading(false);
        }
    };

    // One generic matcher for every dynamic filter the panel renders: booleans and
    // numbers match exactly, dates match the day, everything else is a case-insensitive
    // contains. An empty filter value never excludes anything.
    const matchesFilter = (raw, value) => {
        if (Array.isArray(value)) return value.length === 0 || value.some((v) => matchesFilter(raw, v));
        const needle = String(value ?? "").trim().toLowerCase();
        if (!needle) return true;
        if (raw === null || raw === undefined) return false;
        if (typeof raw === "boolean") return String(raw) === needle;
        if (typeof raw === "number") return parseFloat(needle) === raw;
        if (/^\d{4}-\d{2}-\d{2}$/.test(needle)) return String(raw).slice(0, 10) === needle;
        return String(raw).toLowerCase().includes(needle);
    };

    const hasActiveFilters = Object.values(filterValues).some(
        (v) => !(v === "" || v === null || v === undefined || (Array.isArray(v) && v.length === 0))
    );

    const filteredItems = useMemo(() => {
        const items = masterSheetData?.items || [];
        const active = Object.entries(filterValues).filter(
            ([, v]) => !(v === "" || v === null || v === undefined || (Array.isArray(v) && v.length === 0))
        );
        if (!active.length) return items;
        return items.filter((item) =>
            active.every(([key, value]) => {
                // "Lease expiring within N days" — the one filter that is a window, not a value.
                if (key === "lease_expiration_days") {
                    const end = String(item.leaseEndDmy || item.leaseEnd || "").slice(0, 10);
                    if (!/^\d{4}-\d{2}-\d{2}$/.test(end)) return false;
                    const days = parseInt(value, 10);
                    if (isNaN(days)) return true;
                    const today = new Date(); today.setHours(0, 0, 0, 0);
                    const windowEnd = new Date(today); windowEnd.setDate(windowEnd.getDate() + days);
                    const endDate = new Date(`${end}T00:00:00`);
                    return endDate >= today && endDate <= windowEnd;
                }
                return matchesFilter(item[key], value);
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [masterSheetData, filterValues]);

    return (
        <div className="page-content">
            <div className="container-fluid">
                <PreviewTenantForm
                    show={showForm}
                    handleClose={handleCloseForm}
                    propertyId={filterData.property_id || ""}
                    propertyName={filterData.property_name}
                    buildingName={filterData.building_name}
                    previewData={currentProperty}
                    tenantInfo={currentProperty}
                />
                <Row>
                    <Col xl={12}>
                        <Card>
                            <CardHeader>
                                <PropertySelector
                                    isFilterOpen={isFilterOpen}
                                    setIsFilterOpen={setIsFilterOpen}
                                    action={handleMasterSheetAction}
                                    export={handleExportMastersheetAction}
                                    userData={userData}
                                    filterData={filterData}
                                    setFilterData={setFilterData}
                                    isMastersheetAvailable={{ isValidSelection, masterSheetData }}
                                    onAddTenant={handleAddTenant}
                                />
                                {/* The same dynamic filter panel as the live sheet — the
                                    Filters button above toggles it open. */}
                                {isValidSelection && (
                                    <FilterSection
                                        isFilterOpen={isFilterOpen}
                                        filterData={{ ...filterData, ...filterValues }}
                                        handleFilterChange={handleFilterChange}
                                    />
                                )}
                            </CardHeader>

                            {isLoading ? (
                                <CardBody className="text-center py-5">
                                    <Spinner color="primary" size="lg" className="mb-3" />
                                    <h5 className="text-muted">Loading...</h5>
                                </CardBody>
                            ) : (
                                <>
                                    {!isValidSelection && (
                                        <Alert color="danger" className="mt-3 text-center">
                                            <strong>⚠️ Please select both a Property and its corresponding Building to proceed.</strong>
                                        </Alert>
                                    )}

                                    {isValidSelection && masterSheetData.items.length <= 0 && (
                                        <Alert color="warning" className="mt-3 text-center">
                                            <strong>⚠️ No archived items are currently available for this Master Sheet.</strong>
                                            <p className="mb-0">
                                                Please ensure archived data exists for the selected property and building.
                                            </p>
                                        </Alert>
                                    )}

                                    {isValidSelection && masterSheetData === undefined && (
                                        <>
                                            {!isCreatingNew ? (
                                                <Alert color="warning" className="d-flex align-items-center justify-content-between p-3 border border-warning rounded">
                                                    <div className="d-flex align-items-center">
                                                        <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
                                                        <strong>⚠️ No Layout Available for this Property</strong>
                                                    </div>
                                                    <Button
                                                        color={isCreatingNew ? "secondary" : "primary"}
                                                        className="fw-bold px-4"
                                                        onClick={() => setIsCreatingNew(!isCreatingNew)}
                                                    >
                                                        <i className="bi bi-plus-lg me-1"></i> Create New Layout
                                                    </Button>
                                                </Alert>
                                            ) : (
                                                <MasterSheetTable config={masterSheetData || { items: [] }} />
                                            )}
                                        </>
                                    )}

                                    {isValidSelection && masterSheetData?.items?.length > 0 && filteredItems.length === 0 && (
                                        <Alert color="info" className="mt-3 text-center">
                                            <strong>No archived units match the current filters.</strong>
                                        </Alert>
                                    )}

                                    {isValidSelection && filteredItems.length > 0 && (
                                        <MasterSheetTable
                                            mastersheet={{ ...masterSheetData, items: filteredItems }}
                                            onEditTenant={handleEditTenant}
                                            propertyId={filterData.property_id}
                                            buildingName={filterData.building_name}
                                            onDataUpdate={() => fetchMasterSheet(page)}
                                            showPreviousVersions
                                        />
                                    )}

                                    {/* The pagination is always on screen with the archive, so
                                        the page in view — and that pages exist at all — is
                                        never a mystery. A page holds 100 archived units. */}
                                    {isValidSelection && pagination && masterSheetData?.items?.length > 0 && (
                                        <div className="d-flex align-items-center justify-content-center gap-2 py-3 flex-wrap">
                                            <Button
                                                size="sm"
                                                color="light"
                                                disabled={page <= 1 || isLoading}
                                                onClick={() => fetchMasterSheet(page - 1)}
                                            >
                                                ← Previous
                                            </Button>
                                            <span className="text-muted small">
                                                Page {pagination.page} of {pagination.totalPages} · {pagination.total} archived units
                                                {hasActiveFilters ? ` · ${filteredItems.length} matching the filters on this page` : ""}
                                            </span>
                                            <Button
                                                size="sm"
                                                color="light"
                                                disabled={!pagination.hasNext || isLoading}
                                                onClick={() => fetchMasterSheet(page + 1)}
                                            >
                                                Next →
                                            </Button>
                                        </div>
                                    )}
                                </>
                            )}
                        </Card>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export { ArchivedMastersheet };